import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { casesApi } from "@/services/api";
import type { CaseCreate, CaseUpdate } from "@/types";

export function useCaseList(params?: { skip?: number; limit?: number }) {
  return useQuery({
    queryKey: ["cases", params],
    queryFn: async () => {
      const response = await casesApi.list(params);
      return response.data;
    },
  });
}

export function useCaseDetail(id: number | undefined) {
  return useQuery({
    queryKey: ["cases", "detail", id],
    queryFn: async () => {
      if (!id) return null;
      const response = await casesApi.get(id);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CaseCreate) => {
      const response = await casesApi.create(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cases"] });
    },
  });
}

export function useUpdateCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: CaseUpdate }) => {
      const response = await casesApi.update(id, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cases"] });
    },
  });
}

export function useDeleteCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await casesApi.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cases"] });
    },
  });
}

export function useCaseStats() {
  return useQuery({
    queryKey: ["cases", "stats"],
    queryFn: async () => {
      const response = await casesApi.getStats();
      return response.data;
    },
  });
}

export function useCaseSearch(query: string) {
  return useQuery({
    queryKey: ["cases", "search", query],
    queryFn: async () => {
      const response = await casesApi.search(query);
      return response.data;
    },
    enabled: query.length > 0,
  });
}
